import { encode, decode } from 'pluscodes';
import type { Coordinates } from '../models/data';

export interface Address {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export interface PlusCodeData {
  latitude: number;
  longitude: number;
  latitudeResolution: number;
  longitudeResolution: number;
}

/**
 * Generate a Plus Code from coordinates
 * @param coords - Latitude and longitude
 * @param codeLength - Code length (default: 10 for ~14m precision)
 * @returns Plus Code string (e.g., "849VCWC8+R9")
 */
export function generatePlusCode(
  coords: Coordinates,
  codeLength: number = 10
): string {
  try {
    const result = encode({ latitude: coords.latitude, longitude: coords.longitude }, codeLength);
    if (!result) {
      throw new Error('Failed to generate Plus Code');
    }
    return result;
  } catch (error) {
    console.error('Failed to generate Plus Code:', error);
    throw new Error('Invalid coordinates for Plus Code generation');
  }
}

/**
 * Parse a Plus Code to coordinates
 * @param code - Plus Code string
 * @returns Decoded coordinates and bounds
 */
export function parsePlusCode(code: string): PlusCodeData {
  try {
    const normalized = normalizePlusCode(code);
    if (!validatePlusCode(normalized)) {
      throw new Error('Invalid Plus Code format');
    }
    const result = decode(normalized);
    if (!result) {
      throw new Error('Failed to decode Plus Code');
    }
    return result;
  } catch (error) {
    console.error('Failed to parse Plus Code:', error);
    throw new Error('Invalid Plus Code');
  }
}

/**
 * Validate a Plus Code format
 * @param code - Plus Code string to validate
 * @returns True if valid Plus Code
 */
export function validatePlusCode(code: string): boolean {
  if (!code || typeof code !== 'string') {
    return false;
  }

  const normalized = normalizePlusCode(code);

  // Plus Code pattern: 4-8 characters + "+" + 2-3 characters
  // Valid characters: 23456789CFGHJMPQRVWX (excludes vowels to avoid words)
  const fullCodePattern = /^[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3}$/;

  if (!fullCodePattern.test(normalized)) {
    return false;
  }

  // Try to decode to verify it's actually valid
  try {
    const result = decode(normalized);
    return result !== null;
  } catch {
    return false;
  }
}

/**
 * Format Plus Code for display with space after 4th character
 * @param code - Plus Code string
 * @returns Formatted Plus Code (e.g., "849V CWC8+R9")
 */
export function formatPlusCodeDisplay(code: string): string {
  if (!code) return '';

  const normalized = normalizePlusCode(code);

  // Add space after 4th character for readability
  // Standard format: "8FVC9G8F+6W" -> "8FVC 9G8F+6W"
  if (normalized.length >= 4) {
    const plusIndex = normalized.indexOf('+');
    if (plusIndex >= 4) {
      return normalized.slice(0, 4) + ' ' + normalized.slice(4);
    }
  }

  return normalized;
}

/**
 * Normalize Plus Code input (remove spaces, uppercase)
 * @param code - Raw Plus Code input
 * @returns Normalized Plus Code
 */
export function normalizePlusCode(code: string): string {
  if (!code) return '';
  return code.trim().replace(/\s/g, '').toUpperCase();
}

/**
 * Generate a display address string
 * Fallback logic: use traditional address if available, otherwise Plus Code
 * @param address - Optional traditional address object
 * @param plusCode - Plus Code string
 * @returns Formatted display string
 */
export function generateDisplayAddress(
  address?: Address,
  plusCode?: string
): string {
  if (address) {
    const parts = [
      address.street,
      address.city,
      address.state,
      address.zipCode
    ].filter(Boolean);

    // If we have a reasonably complete address (at least street + city, or street + state)
    if (parts.length >= 3) {
      return parts.join(', ');
    }

    // If we only have partial address (e.g., just street), enhance with Plus Code
    if (parts.length > 0 && plusCode) {
      return `${parts.join(', ')} • ${formatPlusCodeDisplay(plusCode)}`;
    }

    // If we have some address parts but no Plus Code
    if (parts.length > 0) {
      return parts.join(', ');
    }
  }

  if (plusCode) {
    return formatPlusCodeDisplay(plusCode);
  }

  return 'Address not available';
}

/**
 * Check if a string looks like a Plus Code
 * @param query - Search query string
 * @returns True if query matches Plus Code pattern
 */
export function isPlusCodeQuery(query: string): boolean {
  if (!query) return false;

  const normalized = normalizePlusCode(query);

  // Plus Code pattern: 4-8 characters + "+" + 2-3 characters
  // Valid characters: 23456789CFGHJMPQRVWX (excludes vowels to avoid words)
  const plusCodePattern = /^[23456789CFGHJMPQRVWX]{4,8}\+[23456789CFGHJMPQRVWX]{2,3}$/;

  return plusCodePattern.test(normalized);
}

/**
 * Get center coordinates from Plus Code
 * @param code - Plus Code string
 * @returns Center coordinates
 */
export function getPlusCodeCenter(code: string): Coordinates {
  const data = parsePlusCode(code);
  return {
    latitude: data.latitude,
    longitude: data.longitude
  };
}

/**
 * Get a link to view Plus Code on plus.codes website
 * @param code - Plus Code string
 * @returns URL to plus.codes
 */
export function getPlusCodeMapUrl(code: string): string {
  const normalized = normalizePlusCode(code);
  return `https://plus.codes/${normalized}`;
}

/**
 * Convert Plus Code to URL-safe format
 * Replaces '+' with '-' for clean URLs
 * @param plusCode - The Plus Code (e.g., "4RRH+QQ")
 * @returns URL-safe version (e.g., "4RRH-QQ")
 */
export function plusCodeToUrl(plusCode: string): string {
  return plusCode.replace('+', '-');
}

/**
 * Convert URL-safe format back to Plus Code
 * Replaces '-' with '+' to restore original Plus Code
 * @param urlCode - The URL-safe code (e.g., "4RRH-QQ")
 * @returns Plus Code (e.g., "4RRH+QQ")
 */
export function urlToPlusCode(urlCode: string): string {
  return urlCode.replace('-', '+');
}
